import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const calcularVencimiento = (fechaStr, dias) => {
    if (!fechaStr || dias == null) return '';
    const fecha = new Date(fechaStr + 'T00:00:00');
    fecha.setDate(fecha.getDate() + dias);
    return fecha.toISOString().split('T')[0];
};

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year}`;
};

const formatearNumero = (valor, decimales = 0) => {
    if (valor == null) return '0';
    return Number(valor).toLocaleString('es-PY', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
};

const MONEDA_NOMBRES = { 'GS': 'GUARANIES', 'US$': 'DOLARES AMERICANOS', 'UE$': 'EUROS', 'RS': 'REALES' };

const numeroALetras = (numero, moneda) => {
    if (numero == null || numero === 0) return '';
    const entero = Math.floor(Math.abs(numero));
    const decimales = Math.round((Math.abs(numero) - entero) * 100);
    const nomMoneda = MONEDA_NOMBRES[moneda] || moneda || '';

    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE', 'VEINTE'];
    const decenas = ['', '', 'VEINTI', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    const convertirGrupo = (n) => {
        if (n === 0) return '';
        if (n === 100) return 'CIEN';
        let resultado = '';
        const c = Math.floor(n / 100);
        const resto = n % 100;
        if (c > 0) resultado += centenas[c] + ' ';
        if (resto === 0) return resultado.trim();
        if (resto <= 20) { resultado += (resto <= 9 ? unidades[resto] : especiales[resto - 10]); }
        else if (resto < 30) { resultado += 'VEINTI' + unidades[resto - 20]; }
        else { const d = Math.floor(resto / 10); const u = resto % 10; resultado += decenas[d] + (u > 0 ? ' Y ' + unidades[u] : ''); }
        return resultado.trim();
    };

    if (entero === 0) return nomMoneda + ' CERO .--';

    let texto = '';
    const billones = Math.floor(entero / 1000000000000);
    const miles_millones = Math.floor((entero % 1000000000000) / 1000000000);
    const millones = Math.floor((entero % 1000000000) / 1000000);
    const miles = Math.floor((entero % 1000000) / 1000);
    const resto = entero % 1000;

    if (billones > 0) texto += convertirGrupo(billones) + (billones === 1 ? ' BILLON ' : ' BILLONES ');
    if (miles_millones > 0) texto += convertirGrupo(miles_millones) + ' MIL ';
    if (millones > 0 || miles_millones > 0) {
        if (millones > 0) texto += convertirGrupo(millones) + ' ';
        const totalMillones = miles_millones * 1000 + millones;
        if (totalMillones > 0) texto += (totalMillones === 1 ? 'MILLON ' : 'MILLONES ');
    }
    if (miles > 0) texto += (miles === 1 ? 'MIL ' : convertirGrupo(miles) + ' MIL ');
    if (resto > 0) texto += convertirGrupo(resto) + ' ';

    texto = nomMoneda + ' ' + texto.trim();
    if (decimales > 0) { texto += ' CON ' + decimales + '/100'; }
    texto += ' .--';
    return texto.replace(/\s+/g, ' ').trim();
};

function FFacturaVentas({ usuario }) {
    // Grilla principal
    const [planillasAbiertas, setPlanillasAbiertas] = useState([]);
    const [planillaSeleccionada, setPlanillaSeleccionada] = useState(null);
    const [facturas, setFacturas] = useState([]);
    const [facturasFiltradas, setFacturasFiltradas] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [cargando, setCargando] = useState(false);

    // Modal nueva factura
    const [modalAbierto, setModalAbierto] = useState(false);

    // Modal ver factura
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [facturaVer, setFacturaVer] = useState(null);
    const [detallesVer, setDetallesVer] = useState([]);

    // Items en memoria (se guardan todos juntos con la cabecera)
    const [detalles, setDetalles] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [detalleTemp, setDetalleTemp] = useState({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, costoprom: 0, idPresent: 'UN', idGrupo: null });

    // Facturacion desde remisiones
    const [modalRemisionesAbierto, setModalRemisionesAbierto] = useState(false);
    const [remisionesDisponibles, setRemisionesDisponibles] = useState([]);
    const [remisionesMarcadas, setRemisionesMarcadas] = useState([]); // keys "suc-boca-nrem"
    const [remisionesFacturadas, setRemisionesFacturadas] = useState([]); // remisiones que se registraran en fsaldarem al guardar

    // Generar Nota de Envio DESDE una factura (flujo inverso, 1 factura : N remisiones)
    const [modalGenRemAbierto, setModalGenRemAbierto] = useState(false);
    const [facturaGenRem, setFacturaGenRem] = useState(null);
    const [itemsGenRem, setItemsGenRem] = useState([]);        // items con pendiente
    const [cantidadesGenRem, setCantidadesGenRem] = useState({}); // { idProducto: cantidad a remitir }
    const [timbradosRem, setTimbradosRem] = useState([]);
    const [remTimbradoSel, setRemTimbradoSel] = useState(null);   // {timbrado, boca}
    const [guardandoRem, setGuardandoRem] = useState(false);
    const [motivosRem, setMotivosRem] = useState([]);
    const remFormInicial = () => ({
        dirPartida: '', dirLlegada: '', marcaVehiculo: '', chapa: '',
        nomTransportista: '', rucTransportista: '', nomConductor: '', ciConductor: '',
        idMotivo: '', otroMotivo: '', idObra: 0, obs: '', fechafin: ''
    });
    const [formRem, setFormRem] = useState(remFormInicial());

    // Ver notas de envio de una factura
    const [modalNotasEnvioAbierto, setModalNotasEnvioAbierto] = useState(false);
    const [facturaNotasEnvio, setFacturaNotasEnvio] = useState(null);
    const [notasEnvio, setNotasEnvio] = useState([]);
    const [detalleNotaEnvio, setDetalleNotaEnvio] = useState({}); // { remKey: [items] }

    // Catalogos
    const [timbradosVigentes, setTimbradosVigentes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [formasPago, setFormasPago] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [depositos, setDepositos] = useState([]);
    const [listasPrecios, setListasPrecios] = useState([]);
    const [tiposEntrega, setTiposEntrega] = useState([]);

    // Estirar presupuesto
    const [modalPresupuestos, setModalPresupuestos] = useState(false);
    const [presupuestosVigentes, setPresupuestosVigentes] = useState([]);
    const [busquedaPresupuesto, setBusquedaPresupuesto] = useState('');
    const [cargandoPresupuestos, setCargandoPresupuestos] = useState(false);

    // Selectores activos
    const [timbradoSeleccionado, setTimbradoSeleccionado] = useState(null);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
    const [entregaSeleccionada, setEntregaSeleccionada] = useState(null);
    const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState(null);

    // Dropdowns
    const [dropdownTimbradoAbierto, setDropdownTimbradoAbierto] = useState(false);
    const [dropdownClienteAbierto, setDropdownClienteAbierto] = useState(false);
    const [dropdownVendedorAbierto, setDropdownVendedorAbierto] = useState(false);
    const [dropdownProductoAbierto, setDropdownProductoAbierto] = useState(false);
    const [dropdownEntregaAbierto, setDropdownEntregaAbierto] = useState(false);
    const [dropdownFormaPagoAbierto, setDropdownFormaPagoAbierto] = useState(false);
    const [busquedaTimbrado, setBusquedaTimbrado] = useState('');
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [busquedaVendedor, setBusquedaVendedor] = useState('');
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [busquedaEntrega, setBusquedaEntrega] = useState('');
    const [busquedaFormaPago, setBusquedaFormaPago] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);

    const dropdownTimbradoRef = useRef(null);
    const dropdownClienteRef = useRef(null);
    const dropdownVendedorRef = useRef(null);
    const dropdownProductoRef = useRef(null);
    const dropdownEntregaRef = useRef(null);
    const dropdownFormaPagoRef = useRef(null);

    // Formulario cabecera
    const [formulario, setFormulario] = useState({
        timbrado: '', idSucursal: '', boca: 1, tipoDoc: '', nfactura: 0,
        nreparto: '', idCliente: '', nomCliente: '', rucCliente: '', direccion: '', telefono: '',
        fecha: obtenerFechaHoy(), idVendedor: '', idFormapago: '', vence: '',
        idLisprecio: '', idMoneda: '', cambio: 0, idDeposito: 1, obs: '', estado: ' ', idDivision: '', entrega: ''
    });

    // Paginacion
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(20);

    useEffect(() => { cargarPlanillasAbiertas(); cargarDatosSelectores(); }, []);
    useEffect(() => { filtrarFacturas(); }, [busqueda, facturas]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownTimbradoRef.current && !dropdownTimbradoRef.current.contains(event.target)) setDropdownTimbradoAbierto(false);
            if (dropdownClienteRef.current && !dropdownClienteRef.current.contains(event.target)) setDropdownClienteAbierto(false);
            if (dropdownVendedorRef.current && !dropdownVendedorRef.current.contains(event.target)) setDropdownVendedorAbierto(false);
            if (dropdownProductoRef.current && !dropdownProductoRef.current.contains(event.target)) setDropdownProductoAbierto(false);
            if (dropdownEntregaRef.current && !dropdownEntregaRef.current.contains(event.target)) setDropdownEntregaAbierto(false);
            if (dropdownFormaPagoRef.current && !dropdownFormaPagoRef.current.contains(event.target)) setDropdownFormaPagoAbierto(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarPlanillasAbiertas = async () => {
        try { const r = await axios.get(getApiUrl('/api/facturas/planillas-abiertas')); setPlanillasAbiertas(r.data); }
        catch (e) { console.error('Error planillas:', e); }
    };

    const cargarDatosSelectores = async () => {
        try {
            const [r1, r2, r3, r4, r5, r6, r7, r8, r9] = await Promise.all([
                axios.get(getApiUrl('/api/facturas/timbrados-vigentes'), { params: { usuario: usuario || '' } }),
                axios.get(getApiUrl('/api/clientes')),
                axios.get(getApiUrl('/api/vendedores')),
                axios.get(getApiUrl('/api/fpago')),
                axios.get(getApiUrl('/api/monedas')),
                axios.get(getApiUrl('/api/depositos')),
                axios.get(getApiUrl('/api/listas-precio')),
                axios.get(getApiUrl('/api/facturas/tipos-entrega')),
                axios.get(getApiUrl('/api/motivotrans'))
            ]);
            setTimbradosVigentes(r1.data); setClientes(r2.data); setVendedores(r3.data);
            setFormasPago(r4.data); setMonedas(r5.data); setDepositos(r6.data); setListasPrecios(r7.data);
            setTiposEntrega(r8.data);
            setMotivosRem(r9.data || []);
        } catch (e) { console.error('Error selectores:', e); }
    };

    const seleccionarPlanilla = async (planilla) => {
        setPlanillaSeleccionada(planilla); setCargando(true);
        try { const r = await axios.get(getApiUrl('/api/facturas'), { params: { nreparto: planilla.nreparto } }); setFacturas(r.data); setFacturasFiltradas(r.data); }
        catch (e) { console.error('Error facturas:', e); }
        finally { setCargando(false); }
    };

    const filtrarFacturas = () => {
        if (!busqueda) { setFacturasFiltradas(facturas); setPaginaActual(1); return; }
        setFacturasFiltradas(facturas.filter(f => f.nfactura?.toString().includes(busqueda) || f.nomCliente?.toLowerCase().includes(busqueda.toLowerCase()) || f.timbrado?.includes(busqueda)));
        setPaginaActual(1);
    };

    // Selectores
    const filtrarTimbrados = (b) => !b ? timbradosVigentes : timbradosVigentes.filter(t => t.timbrado?.includes(b) || t.tipoDoc?.toLowerCase().includes(b.toLowerCase()));
    // Solo llegan timbrados no vigentes si el usuario tiene el permiso Factura_timbrado_vencido; se marcan en la lista
    const estadoTimbrado = (t) => {
        const hoy = new Date().toISOString().slice(0, 10);
        if (t.vencTimbrado && t.vencTimbrado < hoy) return 'VENCIDO';
        if (t.iniTimbrado && t.iniTimbrado > hoy) return 'NO INICIADO';
        return '';
    };
    const seleccionarTimbrado = (t) => { setTimbradoSeleccionado(t); setFormulario(f => ({...f, timbrado: t.timbrado, idSucursal: t.idSucursal, boca: t.boca, tipoDoc: t.tipoDoc, idDivision: t.idDivision})); setBusquedaTimbrado(''); setDropdownTimbradoAbierto(false); };

    const filtrarClientes = (b) => !b ? clientes.slice(0, 50) : clientes.filter(c => c.idCliente?.toString().includes(b) || c.nomCliente?.toLowerCase().includes(b.toLowerCase()) || c.rucCliente?.toLowerCase().includes(b.toLowerCase()));
    const seleccionarCliente = (c) => {
        setClienteSeleccionado(c);
        // Buscar la forma de pago del cliente tolerando espacios de relleno (fcliente.id_formapago puede ser char)
        const cFpago = (c.idFormapago || '').trim();
        const fpCliente = cFpago ? formasPago.find(x => (x.idFpago || '').trim() === cFpago) : null;
        setFormulario(f => {
            // Usar el valor canonico de la forma de pago (el de tformapago) para que coincida en el guardado
            const nuevaFormaPago = fpCliente ? fpCliente.idFpago : (c.idFormapago || f.idFormapago);
            const nuevaLisprecio = c.idLisprecio || f.idLisprecio;
            // Calcular vencimiento usando vdias1 de la forma de pago del cliente
            const nuevoVence = fpCliente ? calcularVencimiento(f.fecha, fpCliente.vdias1) : f.vence;
            return {
                ...f,
                idCliente: c.idCliente,
                nomCliente: c.nomCliente || '',
                rucCliente: c.rucCliente || '',
                direccion: c.direccion || '',
                telefono: c.telefono || '',
                idVendedor: c.idVendedor || f.idVendedor,
                idFormapago: nuevaFormaPago,
                idLisprecio: nuevaLisprecio,
                vence: nuevoVence
            };
        });
        if (c.idVendedor) { const v = vendedores.find(v => v.idVendedor === c.idVendedor); if (v) setVendedorSeleccionado(v); }
        // Sincronizar el buscador de forma de pago con la que trae el cliente
        if (fpCliente) setFormaPagoSeleccionada(fpCliente);
        setBusquedaCliente(''); setDropdownClienteAbierto(false);
    };

    const filtrarVendedores = (b) => !b ? vendedores.slice(0, 50) : vendedores.filter(v => v.idVendedor?.toString().includes(b) || v.nomVendedor?.toLowerCase().includes(b.toLowerCase()));
    const seleccionarVendedor = (v) => { setVendedorSeleccionado(v); setFormulario(f => ({...f, idVendedor: v.idVendedor})); setBusquedaVendedor(''); setDropdownVendedorAbierto(false); };

    const filtrarEntregas = (b) => !b ? tiposEntrega : tiposEntrega.filter(te =>
        String(te.id_tipoent).includes(b) || String(te.nom_entrega || '').toLowerCase().includes(b.toLowerCase()));
    const seleccionarEntrega = (te) => {
        setEntregaSeleccionada(te);
        setFormulario(f => ({...f, entrega: String(te.id_tipoent).trim()}));
        setBusquedaEntrega(''); setDropdownEntregaAbierto(false);
    };

    const filtrarFormasPago = (b) => !b ? formasPago : formasPago.filter(fp =>
        String(fp.idFpago || '').toLowerCase().includes(b.toLowerCase()));
    const seleccionarFormaPago = (fp) => {
        setFormaPagoSeleccionada(fp);
        // Al elegir forma de pago, recalcular el vencimiento con vdias1
        setFormulario(f => ({...f, idFormapago: fp.idFpago, vence: calcularVencimiento(f.fecha, fp.vdias1)}));
        setBusquedaFormaPago(''); setDropdownFormaPagoAbierto(false);
    };

    // ===== Estirar presupuesto =====
    const abrirModalPresupuestos = async () => {
        setModalPresupuestos(true); setBusquedaPresupuesto(''); setCargandoPresupuestos(true);
        try { const r = await axios.get(getApiUrl('/api/presupuestos/vigentes')); setPresupuestosVigentes(r.data || []); }
        catch (e) { console.error('Error presupuestos vigentes:', e); setPresupuestosVigentes([]); }
        finally { setCargandoPresupuestos(false); }
    };

    const presupuestosFiltrados = () => {
        const b = busquedaPresupuesto.trim().toLowerCase();
        if (!b) return presupuestosVigentes;
        return presupuestosVigentes.filter(p =>
            String(p.nropresupuesto).includes(b) ||
            String(p.nom_cliente || '').toLowerCase().includes(b) ||
            String(p.ruc_cliente || '').toLowerCase().includes(b) ||
            String(p.obra || '').toLowerCase().includes(b));
    };

    const precargarDesdePresupuesto = async (p) => {
        try {
            const r = await axios.get(getApiUrl('/api/presupuestos/para-factura'), {
                params: { idSucursal: p.id_sucursal, tipoDoc: p.tipo_doc || 'PRE', nropresupuesto: p.nropresupuesto, versionn: p.versionn }
            });
            const cab = r.data.cabecera || {};
            const items = r.data.items || [];

            // --- Cabecera ---
            const fpFound = cab.id_formapago ? formasPago.find(x => (x.idFpago || '').trim() === String(cab.id_formapago).trim()) : null;
            setFormulario(f => ({
                ...f,
                idCliente: cab.id_cliente ?? '',
                nomCliente: cab.nom_cliente || '',
                rucCliente: cab.ruc_cliente || '',
                direccion: cab.direccion || '',
                telefono: cab.telefono || '',
                idLisprecio: cab.id_lisprecio ?? f.idLisprecio,
                idFormapago: fpFound ? fpFound.idFpago : (cab.id_formapago || f.idFormapago),
                idVendedor: cab.id_vendedor ?? f.idVendedor,
                idMoneda: cab.id_moneda || f.idMoneda,
                cambio: cab.cambio ?? f.cambio,
                idDeposito: cab.id_deposito ?? f.idDeposito,
                obs: cab.obs || f.obs,
                vence: fpFound ? calcularVencimiento(f.fecha, fpFound.vdias1) : f.vence
            }));

            // Sincronizar selectores visuales
            if (cab.id_cliente != null) {
                const cli = clientes.find(c => c.idCliente === cab.id_cliente);
                if (cli) setClienteSeleccionado(cli);
                else setClienteSeleccionado({ idCliente: cab.id_cliente, nomCliente: cab.nom_cliente, rucCliente: cab.ruc_cliente });
            }
            if (cab.id_vendedor != null) { const v = vendedores.find(v => v.idVendedor === cab.id_vendedor); if (v) setVendedorSeleccionado(v); }
            if (fpFound) setFormaPagoSeleccionada(fpFound);

            // --- Detalle: calcular IVA por linea igual que al agregar manualmente ---
            const nuevosDetalles = items.filter(it => it.id_producto != null).map(it => {
                const cantidad = Number(it.cantidad) || 0;
                const precio = Number(it.precio) || 0;
                const porcDescuento = Number(it.porc_descuento) || 0;
                const gravada = Number(it.gravada) || 0;
                const calc = calcularLinea(cantidad, precio, porcDescuento, gravada);
                return {
                    idProducto: it.id_producto,
                    nomProducto: (it.descripcion || it.nom_producto || '').substring(0, 40),
                    cantidad, precio, costoprom: Number(it.costoprom) || 0, porcDescuento,
                    monDescuento: calc.descuento, monGravada: calc.monGravada, monIva: calc.monIva, monExenta: calc.monExenta,
                    monGravada5: calc.monGravada5, monGravada10: calc.monGravada10,
                    gravada, idPresent: (it.id_present || 'UN'), idGrupo: it.id_grupo
                };
            });
            setDetalles(nuevosDetalles);

            setModalPresupuestos(false);
        } catch (e) {
            console.error('Error precargando presupuesto:', e);
            alert('No se pudo precargar el presupuesto: ' + (e.response?.data || e.message));
        }
    };

    // Detalle - calculo IVA
    const calcularLinea = (cant, precio, descPct, gravada) => {
        const subtotal = precio * cant;
        const descuento = subtotal * (descPct / 100);
        const total = subtotal - descuento;
        let monGravada = 0, monIva = 0, monExenta = 0, monGravada5 = 0, monGravada10 = 0;
        if (gravada === 10) { monGravada = total / 1.10; monIva = total - monGravada; monGravada10 = Math.round(monGravada); }
        else if (gravada === 5) { monGravada = total / 1.05; monIva = total - monGravada; monGravada5 = Math.round(monGravada); }
        else { monExenta = total; }
        return { monGravada: Math.round(monGravada), monIva: Math.round(monIva), monExenta: Math.round(monExenta), monGravada5, monGravada10, total: Math.round(total), descuento: Math.round(descuento) };
    };

    // Busqueda de productos: se espera 250 ms tras la ultima tecla y solo se muestra la respuesta
    // de la ultima busqueda (una respuesta lenta anterior no pisa a la mas reciente).
    const busquedaProductoRef = useRef({ timer: null, secuencia: 0 });
    const buscarProductoAPI = (texto) => {
        const ref = busquedaProductoRef.current;
        clearTimeout(ref.timer);
        if (texto.trim().length < 2) { ref.secuencia++; setProductosEncontrados([]); return; }
        const seq = ++ref.secuencia;
        ref.timer = setTimeout(async () => {
            try {
                const r = await axios.get(getApiUrl('/api/facturad/buscar-producto'), { params: { busqueda: texto } });
                if (seq === ref.secuencia) setProductosEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
    };

    const seleccionarProductoDetalle = async (producto) => {
        setProductoSeleccionado(producto); setProductosEncontrados([]); setDropdownProductoAbierto(false);
        setBusquedaProducto(producto.descripcion);
        let precioConIva = Number(producto.precio) || 0;
        let gravada = Number(producto.gravada) || 0;
        const idLisprecio = formulario.idLisprecio;
        if (idLisprecio) {
            try { const r = await axios.get(getApiUrl('/api/facturad/precio'), { params: { idLisprecio, idProducto: producto.id_producto } }); precioConIva = Number(r.data.precio) || precioConIva; gravada = Number(r.data.gravada) || gravada; }
            catch (e) { /* usar precio producto */ }
        }
        setDetalleTemp({ cantidad: 1, precio: precioConIva, porcDescuento: 0, gravada, costoprom: Number(producto.costoprom) || 0, idPresent: producto.id_present || 'UN', idGrupo: producto.id_grupo });
    };

    const agregarDetalle = () => {
        if (!productoSeleccionado) { alert('Seleccione un producto'); return; }
        if (detalleTemp.cantidad <= 0) { alert('La cantidad debe ser mayor a 0'); return; }
        if (detalleTemp.precio <= 0) { alert('El precio debe ser mayor a 0'); return; }

        const existe = detalles.find(d => d.idProducto === productoSeleccionado.id_producto);
        if (existe) { alert('Este producto ya esta en la lista'); return; }

        const calc = calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.porcDescuento, detalleTemp.gravada);
        setDetalles(prev => [...prev, {
            idProducto: productoSeleccionado.id_producto,
            nomProducto: productoSeleccionado.descripcion?.substring(0, 40),
            cantidad: detalleTemp.cantidad, precio: detalleTemp.precio,
            costoprom: detalleTemp.costoprom, porcDescuento: detalleTemp.porcDescuento,
            monDescuento: calc.descuento, monGravada: calc.monGravada, monIva: calc.monIva, monExenta: calc.monExenta,
            monGravada5: calc.monGravada5, monGravada10: calc.monGravada10,
            gravada: detalleTemp.gravada, idPresent: detalleTemp.idPresent, idGrupo: detalleTemp.idGrupo
        }]);
        setProductoSeleccionado(null); setBusquedaProducto('');
        setDetalleTemp({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, costoprom: 0, idPresent: 'UN', idGrupo: null });
    };

    const eliminarDetalle = (idProducto) => { setDetalles(prev => prev.filter(d => d.idProducto !== idProducto)); };

    // ===== Facturacion desde remisiones =====
    const remKey = (r) => `${r.id_sucursal}-${r.boca}-${r.nremision}`;

    const abrirModalRemisiones = async () => {
        if (!formulario.idCliente) { alert('Primero seleccione un cliente'); return; }
        try {
            const r = await axios.get(getApiUrl('/api/facturas/remisiones-sin-facturar'), { params: { idCliente: formulario.idCliente } });
            setRemisionesDisponibles(r.data);
            setRemisionesMarcadas([]);
            setModalRemisionesAbierto(true);
        } catch (e) {
            console.error('Error al cargar remisiones:', e);
            alert('Error al cargar las remisiones del cliente');
        }
    };

    const toggleRemision = (r) => {
        const k = remKey(r);
        setRemisionesMarcadas(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
    };

    const toggleTodasRemisiones = () => {
        if (remisionesMarcadas.length === remisionesDisponibles.length) {
            setRemisionesMarcadas([]);
        } else {
            setRemisionesMarcadas(remisionesDisponibles.map(remKey));
        }
    };

    const confirmarRemisiones = async () => {
        const seleccionadas = remisionesDisponibles.filter(r => remisionesMarcadas.includes(remKey(r)));
        if (seleccionadas.length === 0) { alert('Seleccione al menos una remision'); return; }

        try {
            // Traer items consolidados de las remisiones seleccionadas
            const payload = seleccionadas.map(r => ({ idSucursal: r.id_sucursal, boca: r.boca, nremision: r.nremision }));
            const r = await axios.post(getApiUrl('/api/facturas/items-remisiones'), payload);
            const items = r.data;

            // Convertir items consolidados al formato de detalle (precio editable, arranca con el de la remision)
            const nuevosDetalles = items.map(it => {
                const cant = Number(it.cantidad) || 0;
                const precio = Number(it.precio) || 0;
                const gravada = Number(it.gravada) || 0;
                const calc = calcularLinea(cant, precio, 0, gravada);
                return {
                    idProducto: String(it.id_producto),
                    nomProducto: (it.descripcion || '').substring(0, 40),
                    cantidad: cant, precio: precio,
                    costoprom: Number(it.costoprom) || 0, porcDescuento: 0,
                    monDescuento: calc.descuento, monGravada: calc.monGravada, monIva: calc.monIva, monExenta: calc.monExenta,
                    monGravada5: calc.monGravada5, monGravada10: calc.monGravada10,
                    gravada: gravada, idPresent: it.id_present || 'UN', idGrupo: it.id_grupo
                };
            });

            // Reemplazar el detalle con los items de las remisiones
            setDetalles(nuevosDetalles);
            // Guardar las remisiones a registrar al guardar la factura
            setRemisionesFacturadas(seleccionadas.map(rr => ({ id_sucursal: rr.id_sucursal, boca: rr.boca, nremision: rr.nremision, total_estimado: rr.total_estimado })));
            // Agregar a la observacion de la factura los numeros de remisiones facturadas
            const nrosRemisiones = seleccionadas.map(rr => rr.nremision).join(', ');
            setFormulario(f => {
                const msgRem = 'Ref. Remisiones: ' + nrosRemisiones;
                // Quitar cualquier mensaje previo de remisiones para no duplicar
                const obsLimpia = (f.obs || '').replace(/Ref\. Remisiones:[^|]*/g, '').replace(/^\s*\|\s*/, '').trim();
                const nuevaObs = obsLimpia ? (obsLimpia + ' | ' + msgRem) : msgRem;
                return { ...f, obs: nuevaObs.substring(0, 100) };
            });
            setModalRemisionesAbierto(false);
            alert(seleccionadas.length + ' remision(es) cargada(s). Verifique y ajuste los precios de cada articulo.');
        } catch (e) {
            console.error('Error al consolidar items:', e);
            alert('Error al obtener los items de las remisiones');
        }
    };

    // Recalcula los montos de un item del detalle cuando se edita el precio o cantidad
    const editarPrecioDetalle = (idProducto, nuevoPrecio) => {
        setDetalles(prev => prev.map(d => {
            if (d.idProducto !== idProducto) return d;
            const calc = calcularLinea(d.cantidad, nuevoPrecio, d.porcDescuento, d.gravada);
            return { ...d, precio: nuevoPrecio, monDescuento: calc.descuento, monGravada: calc.monGravada, monIva: calc.monIva, monExenta: calc.monExenta, monGravada5: calc.monGravada5, monGravada10: calc.monGravada10 };
        }));
    };

    // ===== Ver notas de envio (remisiones) asociadas a una factura =====
    const verNotasEnvio = async (f) => {
        setFacturaNotasEnvio(f);
        setNotasEnvio([]);
        setDetalleNotaEnvio({});
        try {
            const r = await axios.get(getApiUrl('/api/facturas/remisiones-de-factura'), {
                params: { ffIdSucursal: f.idSucursal, ffTipoDoc: f.tipoDoc, ffBoca: f.boca, ffNfactura: f.nfactura, ffTimbrado: f.timbrado }
            });
            setNotasEnvio(r.data);
            // Cargar el detalle de cada remision
            const detalles = {};
            for (const rem of r.data) {
                try {
                    const d = await axios.get(getApiUrl('/api/facturas/detalle-remision'), {
                        params: { idSucursal: rem.id_sucursal, boca: rem.boca, nremision: rem.nremision }
                    });
                    detalles[`${rem.id_sucursal}-${rem.boca}-${rem.nremision}`] = d.data;
                } catch (e) { /* ignorar */ }
            }
            setDetalleNotaEnvio(detalles);
        } catch (e) {
            console.error('Error al cargar notas de envio:', e);
        }
        setModalNotasEnvioAbierto(true);
    };

    const calcularTotales = () => {
        const gravada5 = detalles.reduce((s, d) => s + (d.monGravada5 || 0), 0);
        const gravada10 = detalles.reduce((s, d) => s + (d.monGravada10 || 0), 0);
        const iva = detalles.reduce((s, d) => s + d.monIva, 0);
        const exenta = detalles.reduce((s, d) => s + d.monExenta, 0);
        const gravada = gravada5 + gravada10;
        return { gravada, gravada5, gravada10, iva, exenta, total: gravada + iva + exenta };
    };

    // Modal
    const abrirModalNuevo = () => {
        if (!planillaSeleccionada) { alert('Seleccione una planilla primero'); return; }
        // La fecha de la factura debe coincidir con la fecha de la planilla
        const fechaPlanilla = planillaSeleccionada.fecha || obtenerFechaHoy();
        setFormulario({ timbrado: '', idSucursal: '', boca: 1, tipoDoc: '', nfactura: 0, nreparto: planillaSeleccionada.nreparto, idCliente: '', nomCliente: '', rucCliente: '', direccion: '', telefono: '', fecha: fechaPlanilla, idVendedor: '', idFormapago: '', vence: '', idLisprecio: '', idMoneda: '', cambio: 0, idDeposito: planillaSeleccionada.idDeposito || 1, obs: '', estado: ' ', idDivision: '', entrega: '', entregarPorRemision: false });
        setTimbradoSeleccionado(null); setClienteSeleccionado(null); setVendedorSeleccionado(null); setEntregaSeleccionada(null); setFormaPagoSeleccionada(null);
        setDetalles([]); setProductoSeleccionado(null); setBusquedaProducto(''); setProductosEncontrados([]);
        setDetalleTemp({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, costoprom: 0, idPresent: 'UN', idGrupo: null });
        setRemisionesFacturadas([]); setRemisionesMarcadas([]); setRemisionesDisponibles([]);
        setModalAbierto(true);
    };

    const cerrarModal = () => { setModalAbierto(false); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validar cabecera
        if (!formulario.timbrado) { alert('Debe seleccionar un timbrado'); return; }
        if (!formulario.idCliente) { alert('Debe seleccionar un cliente'); return; }
        if (!formulario.fecha) { alert('Debe ingresar una fecha'); return; }
        if (!formulario.idFormapago) { alert('Debe seleccionar una forma de pago'); return; }
        if (!formulario.vence) { alert('Debe definir la fecha de vencimiento'); return; }
        if (!formulario.idLisprecio) { alert('Debe seleccionar una lista de precios'); return; }
        if (!formulario.idMoneda) { alert('Debe seleccionar una moneda'); return; }
        if (!formulario.cambio && formulario.cambio !== 0) { alert('Debe ingresar el tipo de cambio'); return; }
        if (!formulario.idDeposito) { alert('Debe seleccionar un deposito'); return; }
        if (depositos.find(d => Number(d.idDeposito) === Number(formulario.idDeposito))?.reserva === 'S') { alert('No se puede facturar desde un deposito de reserva'); return; }
        if (detalles.length === 0) { alert('Debe agregar al menos un item'); return; }

        try {
            // 1. Guardar cabecera
            const totales = calcularTotales();
            const numlet = numeroALetras(totales.total, formulario.idMoneda);
            // Si la factura se genera desde remisiones, el stock ya fue descontado por la remision -> stkRem='SI' (el trigger no descuenta de nuevo)
            const stkRem = (remisionesFacturadas.length > 0 || formulario.entregarPorRemision) ? 'SI' : 'NO';
            const cabeceraData = { ...formulario, monGravada: totales.gravada, monIva: totales.iva, monExenta: totales.exenta, monTotal: totales.total, saldo: totales.total, numLet: numlet, stkRem: stkRem, idOperador: usuario || '', creadoPor: usuario || '' };
            const response = await axios.post(getApiUrl('/api/facturas'), cabeceraData);
            const facCreada = response.data;
            const nfactura = facCreada.nfactura;

            // 2. Guardar cada item del detalle
            for (let i = 0; i < detalles.length; i++) {
                const d = detalles[i];
                await axios.post(getApiUrl('/api/facturad'), {
                    timbrado: formulario.timbrado, idSucursal: formulario.idSucursal, boca: formulario.boca,
                    tipoDoc: formulario.tipoDoc, nfactura: nfactura,
                    idProducto: d.idProducto, item: i + 1, idCliente: formulario.idCliente,
                    nomProducto: d.nomProducto, idDeposito: formulario.idDeposito,
                    cantidad: d.cantidad, canBonif: 0, precio: d.precio, costoprom: d.costoprom,
                    porcDescuento: d.porcDescuento, monDescuento: d.monDescuento, monBonif: 0,
                    monExenta: d.monExenta, monGravada: d.monGravada, monIva: d.monIva,
                    gravar: ' ',
                    idPresent: d.idPresent || 'UN', cantPresent: 1,
                    idDivision: formulario.idDivision, idGrupo: d.idGrupo, prodActivo: 1
                });
            }

            // 3. Si la factura se genero desde remisiones, registrarlas en fsaldarem
            if (remisionesFacturadas.length > 0) {
                await axios.post(getApiUrl('/api/facturas/registrar-remisiones'), {
                    remisiones: remisionesFacturadas.map(r => ({
                        idSucursal: r.id_sucursal, boca: r.boca, nremision: r.nremision,
                        monto: r.total_estimado || 0
                    })),
                    ffIdSucursal: formulario.idSucursal,
                    ffTipoDoc: formulario.tipoDoc,
                    ffBoca: formulario.boca,
                    ffNfactura: nfactura,
                    ffTimbrado: formulario.timbrado,
                    fecha: formulario.fecha,
                    idOperador: usuario || ''
                });
            }

            alert('Factura Nro ' + nfactura + ' creada exitosamente con ' + detalles.length + ' items');
            cerrarModal();
            seleccionarPlanilla(planillaSeleccionada);
        } catch (error) {
            console.error('Error:', error);
            const msg = error.response?.data || 'Error al guardar la factura';
            alert(typeof msg === 'string' ? msg : 'Error al guardar la factura');
        }
    };

    const verFactura = async (factura) => {
        setFacturaVer(factura);
        try {
            const r = await axios.get(getApiUrl('/api/facturad'), {
                params: { timbrado: factura.timbrado, idSucursal: factura.idSucursal, boca: factura.boca, tipoDoc: factura.tipoDoc, nfactura: factura.nfactura }
            });
            setDetallesVer(r.data);
        } catch (e) { console.error('Error al cargar detalle:', e); setDetallesVer([]); }
        setModalVerAbierto(true);
    };

    // Estado del dropdown de impresion abierto (identifica la fila con clave unica)
    const [dropdownImprimirId, setDropdownImprimirId] = React.useState(null);
    // Posicion (viewport) del dropdown de impresion para renderizarlo con position:fixed y evitar recorte por overflow de la tabla
    const [dropdownImprimirPos, setDropdownImprimirPos] = React.useState({ top: 0, right: 0 });

    // Cerrar el dropdown si se clickea fuera
    React.useEffect(() => {
        const cerrar = () => setDropdownImprimirId(null);
        if (dropdownImprimirId !== null) {
            document.addEventListener('click', cerrar);
            return () => document.removeEventListener('click', cerrar);
        }
    }, [dropdownImprimirId]);

    const REPORTES_FACTURA = [
        { key: 'ImpresionFacturaAuto',           label: 'Original',       icono: '🖨️' },
        { key: 'ImpresionFacturaAutoDuplicado',  label: 'Duplicado',      icono: '📄' },
        { key: 'ImpresionFacturaAutoTriplicado', label: 'Triplicado',     icono: '📃' },
        { key: 'ImpresionFacturaAutoFEX',        label: 'Factura Electronica (FEX)', icono: '🧾' }
    ];

    const claveFactura = (f) => `${f.idSucursal}-${f.boca}-${f.tipoDoc}-${f.nfactura}-${f.timbrado}`;

    // ===== Generar Nota de Envio desde una factura =====
    const abrirModalGenerarRemision = async (f) => {
        setFacturaGenRem(f);
        setCantidadesGenRem({});
        setRemTimbradoSel(null);
        setItemsGenRem([]);
        setFormRem({ ...remFormInicial(), dirLlegada: f.direccion || '' });
        setModalGenRemAbierto(true);
        try {
            const [ri, rt, rm] = await Promise.all([
                axios.get(getApiUrl('/api/facturas/items-para-remitir'), { params: {
                    idSucursal: f.idSucursal, tipoDoc: f.tipoDoc, boca: f.boca, nfactura: f.nfactura, timbrado: f.timbrado } }),
                axios.get(getApiUrl('/api/facturas/timbrados-remision'), { params: { idSucursal: f.idSucursal } }),
                axios.get(getApiUrl('/api/motivotrans'))
            ]);
            setItemsGenRem(ri.data);
            setTimbradosRem(rt.data);
            setMotivosRem(rm.data || []);
            // Precargar cantidades con el pendiente
            const cant = {};
            ri.data.forEach(it => { cant[String(it.id_producto).trim()] = Number(it.pendiente) || 0; });
            setCantidadesGenRem(cant);
        } catch (e) { console.error(e); alert('Error al cargar items para remitir'); }
    };

    const generarRemision = async () => {
        if (!facturaGenRem) return;
        if (!remTimbradoSel) return alert('Seleccione el timbrado de la remisión');
        const items = itemsGenRem
            .map(it => ({
                idProducto: String(it.id_producto).trim(),
                cantidad: Number(cantidadesGenRem[String(it.id_producto).trim()]) || 0,
                precio: Number(it.precio) || 0,
                idMedida: it.id_medida,
                costoprom: Number(it.costoprom) || 0,
                costoulcom: Number(it.costoulcom) || 0
            }))
            .filter(it => it.cantidad > 0);
        if (items.length === 0) return alert('Ingrese al menos una cantidad a remitir');
        setGuardandoRem(true);
        try {
            const { data } = await axios.post(getApiUrl('/api/facturas/generar-remision'), {
                ffIdSucursal: facturaGenRem.idSucursal, ffTipoDoc: facturaGenRem.tipoDoc,
                ffBoca: facturaGenRem.boca, ffNfactura: facturaGenRem.nfactura, ffTimbrado: facturaGenRem.timbrado,
                remBoca: remTimbradoSel.boca, remTimbrado: remTimbradoSel.timbrado,
                idMotivo: formRem.idMotivo || '1', idOperador: usuario || '',
                dirPartida: formRem.dirPartida, dirLlegada: formRem.dirLlegada,
                marcaVehiculo: formRem.marcaVehiculo, chapa: formRem.chapa,
                nomTransportista: formRem.nomTransportista, rucTransportista: formRem.rucTransportista,
                nomConductor: formRem.nomConductor, ciConductor: formRem.ciConductor,
                otroMotivo: formRem.otroMotivo, idObra: Number(formRem.idObra) || 0,
                obs: formRem.obs, fechafin: formRem.fechafin,
                items
            });
            const abrirPdf = window.confirm(`Nota de Envío N° ${data.nremision} generada (${data.items} items). ¿Imprimir el remito?`);
            if (abrirPdf) {
                window.open(getApiUrl('/api/remisiones/imprimir') + `?idSucursal=${data.idSucursal}&boca=${data.boca}&nremision=${data.nremision}`, '_blank');
            }
            setModalGenRemAbierto(false);
        } catch (e) {
            alert(e.response?.data?.message || 'Error al generar la nota de envío');
        } finally {
            setGuardandoRem(false);
        }
    };

    const imprimirFactura = (factura, reporte = 'ImpresionFacturaAuto') => {
        const params = new URLSearchParams({
            reporte,
            timbrado: factura.timbrado || '',
            idSucursal: String(factura.idSucursal || ''),
            boca: String(factura.boca || ''),
            tipoDoc: factura.tipoDoc || '',
            nfactura: String(factura.nfactura || ''),
            codUsuario: usuario || '',
            numLetras: factura.numLet || '',
            descMoneda: factura.idMoneda === 'USD' ? 'DOLARES' : 'GUARANIES'
        });
        const url = getApiUrl('/api/facturas/imprimir') + '?' + params.toString();
        window.open(url, '_blank');
        setDropdownImprimirId(null);
    };

    // Informe consolidado de items (por articulo) de las facturas no anuladas de la planilla
    const informeItemsPlanilla = () => {
        if (!planillaSeleccionada) { alert('Seleccione una planilla primero'); return; }
        const url = getApiUrl(`/api/facturas/informe-items-planilla/${planillaSeleccionada.nreparto}`);
        window.open(url, '_blank');
    };

    const anularFactura = async (factura) => {
        if (factura.estado === 'A') { alert('La factura ya esta anulada'); return; }
        if (!window.confirm('¿Esta seguro de anular la factura Nro ' + factura.nfactura + '?\nEsta accion no se puede deshacer.')) return;
        try {
            await axios.put(getApiUrl('/api/facturas/anular'), null, {
                params: { timbrado: factura.timbrado, idSucursal: factura.idSucursal, boca: factura.boca, tipoDoc: factura.tipoDoc, nfactura: factura.nfactura }
            });
            alert('Factura Nro ' + factura.nfactura + ' anulada exitosamente');
            seleccionarPlanilla(planillaSeleccionada);
        } catch (error) {
            const msg = error.response?.data || 'Error al anular la factura';
            alert(typeof msg === 'string' ? msg : 'Error al anular la factura');
        }
    };

    const exportarExcel = () => {
        const d = facturasFiltradas.map(f => ({ 'Nro': f.nfactura, 'Timbrado': f.timbrado, 'Tipo': f.tipoDoc, 'Fecha': f.fecha ? formatearFecha(f.fecha) : '', 'Cliente': f.nomCliente, 'RUC': f.rucCliente, 'Total': f.monTotal, 'Estado': f.estado }));
        const ws = XLSX.utils.json_to_sheet(d); const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Facturas'); XLSX.writeFile(wb, 'facturas_venta.xlsx');
    };

    // Paginacion
    const indiceUltimo = paginaActual * itemsPorPagina;
    const indicePrimero = indiceUltimo - itemsPorPagina;
    const facturasActuales = facturasFiltradas.slice(indicePrimero, indiceUltimo);
    const totalPaginas = Math.ceil(facturasFiltradas.length / itemsPorPagina);

    // Preview calculo linea actual
    const previewLinea = productoSeleccionado ? calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.porcDescuento, detalleTemp.gravada) : null;
    const totales = calcularTotales();

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Facturas de Venta</h2>
                <div className="acciones-header">
                    <input type="text" className="input-busqueda" placeholder="Buscar factura..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo} disabled={!planillaSeleccionada}>+ Nueva Factura</button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>Exportar Excel</button>
                    <button className="btn btn-exportar" onClick={informeItemsPlanilla}
                        disabled={!planillaSeleccionada}
                        style={{ background: '#6A1B9A' }}
                        title="Informe consolidado de items por articulo (facturas no anuladas de la planilla)">
                        📋 Informe de Items
                    </button>
                </div>
            </div>

            {/* Selector de planilla */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '14px', color: '#555', fontWeight: '500' }}>Planilla:</label>
                <select value={planillaSeleccionada?.nreparto || ''} onChange={(e) => { const p = planillasAbiertas.find(p => p.nreparto === Number(e.target.value)); if (p) seleccionarPlanilla(p); }}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px', minWidth: '300px' }}>
                    <option value="">-- Seleccione una planilla abierta --</option>
                    {planillasAbiertas.map(p => (<option key={p.nreparto} value={p.nreparto}>Nro {p.nreparto} - {p.fecha ? formatearFecha(p.fecha) : 'Sin fecha'} - {p.obs || ''}</option>))}
                </select>
                {planillaSeleccionada && <span style={{ fontSize: '13px', color: '#7f8c8d' }}>{facturasFiltradas.length} facturas</span>}
            </div>

            {/* Grilla facturas */}
            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead><tr><th>Numero</th><th>Timbrado</th><th>Tipo</th><th>Fecha</th><th>Cliente</th><th>RUC</th><th style={{textAlign:'right'}}>Total</th><th style={{textAlign:'right'}}>Saldo</th><th>Estado</th><th>Acc</th></tr></thead>
                    <tbody>
                        {cargando ? <tr><td colSpan="10" className="sin-datos">Cargando...</td></tr>
                        : !planillaSeleccionada ? <tr><td colSpan="10" className="sin-datos">Seleccione una planilla</td></tr>
                        : facturasActuales.length === 0 ? <tr><td colSpan="10" className="sin-datos">No hay facturas</td></tr>
                        : facturasActuales.map((f, i) => (
                            <tr key={i}><td>{String(f.idSucursal).padStart(3,'0')}-{String(f.boca).padStart(3,'0')}-{f.nfactura}</td><td>{f.timbrado}</td><td>{f.tipoDoc}</td><td>{f.fecha ? formatearFecha(f.fecha) : ''}</td><td>{f.nomCliente}</td><td>{f.rucCliente}</td><td style={{textAlign:'right'}}>{formatearNumero(f.monTotal)}</td><td style={{textAlign:'right'}}>{formatearNumero(f.saldo)}</td><td style={f.estado === 'A' ? { color: '#c62828', fontWeight: 'bold' } : {}}>{f.estado === 'C' ? 'Cerrada' : f.estado === 'A' ? 'Anulada' : 'Abierta'}</td>
                                <td>
                                    <button type="button" onClick={() => verFactura(f)} className="btn-accion" title="Ver factura">👁</button>
                                    <button type="button" onClick={() => verNotasEnvio(f)} className="btn-accion" title="Ver notas de envio (remisiones)">🚚</button>
                                    <span style={{ position: 'relative', display: 'inline-block' }}
                                        onClick={(e) => e.stopPropagation()}>
                                        <button type="button"
                                            onClick={(e) => {
                                                if (dropdownImprimirId === claveFactura(f)) {
                                                    setDropdownImprimirId(null);
                                                } else {
                                                    const r = e.currentTarget.getBoundingClientRect();
                                                    setDropdownImprimirPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                                    setDropdownImprimirId(claveFactura(f));
                                                }
                                            }}
                                            className="btn-accion"
                                            title="Imprimir factura (PDF)"
                                            style={{ color: '#2DCCD3' }}>🖨️ ▾</button>
                                        {dropdownImprimirId === claveFactura(f) && ReactDOM.createPortal(
                                            <div onClick={(e) => e.stopPropagation()} style={{
                                                position: 'fixed',
                                                top: dropdownImprimirPos.top, right: dropdownImprimirPos.right,
                                                backgroundColor: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '5px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                minWidth: '220px',
                                                zIndex: 99999
                                            }}>
                                                {REPORTES_FACTURA.map(r => (
                                                    <div key={r.key}
                                                        onClick={() => imprimirFactura(f, r.key)}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid #eee',
                                                            fontSize: '13px',
                                                            display: 'flex', alignItems: 'center', gap: '8px'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EDF1FB'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                        <span>{r.icono}</span>
                                                        <span>{r.label}</span>
                                                    </div>
                                                ))}
                                            </div>,
                                            document.body
                                        )}
                                    </span>
                                    <button type="button" onClick={() => abrirModalGenerarRemision(f)} className="btn-accion"
                                        title={f.stkRem === 'SI' ? 'Generar Nota de Envío' : 'Solo facturas marcadas "Entregar por Nota de Envío"'}
                                        disabled={f.estado === 'A' || f.stkRem !== 'SI'}
                                        style={(f.estado === 'A' || f.stkRem !== 'SI') ? { opacity: 0.3 } : { color: '#6A1B9A' }}>📦</button>
                                    <button type="button" onClick={() => anularFactura(f)} className="btn-accion" title="Anular factura"
                                        disabled={f.estado === 'A'} style={f.estado === 'A' ? { opacity: 0.3 } : { color: '#c62828' }}>✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginacion */}
            {facturasFiltradas.length > 0 && (
                <div className="paginacion-container">
                    <div className="paginacion-info">
                        <span>Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, facturasFiltradas.length)} de {facturasFiltradas.length}</span>
                        <select value={itemsPorPagina} onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }} className="select-registros">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                        </select>
                    </div>
                    <div className="paginacion-controles">
                        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} className="btn-paginacion">|&lt;</button>
                        <button onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1} className="btn-paginacion">&lt;</button>
                        <button onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;</button>
                        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;|</button>
                    </div>
                </div>
            )}

            {/* ====== MODAL ESTIRAR PRESUPUESTO ====== */}
            {modalPresupuestos && (
                <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={() => setModalPresupuestos(false)}>
                    <div className="modal-contenido-grande" style={{ maxWidth: '1000px', width: '90vw' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Presupuestos Vigentes</h3>
                            <button onClick={() => setModalPresupuestos(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            <input type="text" placeholder="Buscar por N°, cliente, RUC u obra..."
                                value={busquedaPresupuesto} onChange={(e) => setBusquedaPresupuesto(e.target.value)}
                                className="form-input" style={{ marginBottom: '12px' }} autoFocus />
                            {cargandoPresupuestos ? (
                                <p style={{ textAlign: 'center', color: '#7f8c8d' }}>Cargando...</p>
                            ) : presupuestosFiltrados().length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#7f8c8d' }}>No hay presupuestos vigentes.</p>
                            ) : (
                                <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                    <table className="tabla-datos" style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th>N°</th><th>Ver.</th><th>Fecha</th><th>Vence</th>
                                                <th>Cliente</th><th>Obra</th><th style={{ textAlign: 'right' }}>Total</th>
                                                <th style={{ textAlign: 'center' }}>Items</th><th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {presupuestosFiltrados().map((p, i) => (
                                                <tr key={i}>
                                                    <td>{p.nropresupuesto}</td>
                                                    <td>{p.versionn}</td>
                                                    <td>{p.fecha ? formatearFecha(p.fecha) : ''}</td>
                                                    <td>{p.vence ? formatearFecha(p.vence) : '—'}</td>
                                                    <td>{p.nom_cliente}</td>
                                                    <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.obra}>{p.obra}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(p.mon_total)}</td>
                                                    <td style={{ textAlign: 'center' }}>{p.items}</td>
                                                    <td>
                                                        <button type="button" onClick={() => precargarDesdePresupuesto(p)}
                                                            style={{ background: '#198754', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                                                            Precargar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '10px' }}>
                                Al precargar se reemplazan cliente, forma de pago, lista de precios, vendedor, moneda y los ítems de la factura con los del presupuesto.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ====== MODAL NUEVA FACTURA (Cabecera + Detalle en UNA sola pantalla) ====== */}
            {modalAbierto && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '1560px', width: '96vw' }}>
                        <div className="modal-header">
                            <h3>Nueva Factura - Planilla {planillaSeleccionada?.nreparto}</h3>
                            <button onClick={cerrarModal} className="btn-cerrar">&times;</button>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {/* ===== CABECERA ===== */}
                                <div className="form-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>Datos de Cabecera</h3>
                                        <button type="button" onClick={abrirModalPresupuestos}
                                            style={{ background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}
                                            title="Estirar un presupuesto vigente y precargar la factura">
                                            📋 Estirar Presupuesto
                                        </button>
                                    </div>
                                    <div className="cabecera-compacta" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: '12px', rowGap: '6px', marginTop: '10px' }}>
                                            <div className="form-group">
                                                <label>Timbrado *</label>
                                                <div className="cuenta-selector" ref={dropdownTimbradoRef}>
                                                    {timbradoSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{timbradoSeleccionado.timbrado} - {timbradoSeleccionado.tipoDoc} (Boca {timbradoSeleccionado.boca})</span>
                                                            <button type="button" onClick={() => { setTimbradoSeleccionado(null); setFormulario(f => ({...f, timbrado:'', idSucursal:'', boca:1, tipoDoc:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar timbrado..." value={busquedaTimbrado} onChange={(e) => setBusquedaTimbrado(e.target.value)} onFocus={() => setDropdownTimbradoAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownTimbradoAbierto && !timbradoSeleccionado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarTimbrados(busquedaTimbrado).map((t, i) => (
                                                                <div key={i} className="cuenta-item" onClick={() => seleccionarTimbrado(t)}><strong>{t.timbrado}</strong> - {t.tipoDoc} | Boca {t.boca} | {t.desde}-{t.hasta} | Ult:{t.ultValor}{estadoTimbrado(t) && <span style={{ color: '#c62828', fontWeight: 'bold', marginLeft: 8 }}>{estadoTimbrado(t)} ({formatearFecha(t.vencTimbrado)})</span>}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Cliente *</label>
                                                <div className="cuenta-selector" ref={dropdownClienteRef}>
                                                    {clienteSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{clienteSeleccionado.idCliente} - {clienteSeleccionado.nomCliente}</span>
                                                            <button type="button" onClick={() => { setClienteSeleccionado(null); setFormulario(f => ({...f, idCliente:'', nomCliente:'', rucCliente:'', direccion:'', telefono:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text"
                                                            placeholder={formulario.timbrado ? "Buscar cliente..." : "Primero elija el timbrado"}
                                                            value={busquedaCliente}
                                                            onChange={(e) => setBusquedaCliente(e.target.value)}
                                                            onFocus={() => formulario.timbrado && setDropdownClienteAbierto(true)}
                                                            disabled={!formulario.timbrado}
                                                            className="form-input"
                                                            style={!formulario.timbrado ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}} />
                                                    )}
                                                    {dropdownClienteAbierto && !clienteSeleccionado && formulario.timbrado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarClientes(busquedaCliente).map(c => (
                                                                <div key={c.idCliente} className="cuenta-item" onClick={() => seleccionarCliente(c)}><strong>{c.idCliente}</strong> - {c.nomCliente} ({c.rucCliente})</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Lista de Precios *</label>
                                                <select value={formulario.idLisprecio || ''} onChange={(e) => setFormulario(f => ({...f, idLisprecio: e.target.value ? Number(e.target.value) : null}))}
                                                    style={{ width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'5px' }}>
                                                    <option value="">-- Seleccione --</option>
                                                    {listasPrecios.map(lp => (<option key={lp.idLisprecio} value={lp.idLisprecio}>{lp.idLisprecio} - {lp.obs || 'Sin desc'}</option>))}
                                                </select>
                                                {clienteSeleccionado && clienteSeleccionado.idLisprecio
                                                    && Number(clienteSeleccionado.idLisprecio) === Number(formulario.idLisprecio) && (
                                                    <span style={{ fontSize: '11px', color: '#2E7D32', marginTop: '4px', display: 'block' }}>
                                                        ✓ Sugerida del cliente {clienteSeleccionado.nomCliente}. Podés cambiarla si corresponde.
                                                    </span>
                                                )}
                                                {clienteSeleccionado && !clienteSeleccionado.idLisprecio && (
                                                    <span style={{ fontSize: '11px', color: '#E65100', marginTop: '4px', display: 'block' }}>
                                                        El cliente no tiene lista de precios asignada. Elegí una manualmente.
                                                    </span>
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <label>Fecha (de la planilla)</label>
                                                <input type="date" value={formulario.fecha} readOnly disabled
                                                    className="form-input"
                                                    style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} />
                                                <span style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                                                    La fecha coincide con la planilla Nro {planillaSeleccionada?.nreparto}
                                                </span>
                                            </div>
                                            <div className="form-group">
                                                <label>Forma de Pago *</label>
                                                <div className="cuenta-selector" ref={dropdownFormaPagoRef}>
                                                    {formaPagoSeleccionada ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{formaPagoSeleccionada.idFpago} ({formaPagoSeleccionada.cancuota} cuota{formaPagoSeleccionada.cancuota > 1 ? 's' : ''})</span>
                                                            <button type="button" onClick={() => { setFormaPagoSeleccionada(null); setFormulario(f => ({...f, idFormapago:'', vence:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar forma de pago..." value={busquedaFormaPago} onChange={(e) => setBusquedaFormaPago(e.target.value)} onFocus={() => setDropdownFormaPagoAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownFormaPagoAbierto && !formaPagoSeleccionada && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarFormasPago(busquedaFormaPago).map(fp => (
                                                                <div key={fp.idFpago} className="cuenta-item" onClick={() => seleccionarFormaPago(fp)}><strong>{fp.idFpago}</strong> ({fp.cancuota} cuota{fp.cancuota > 1 ? 's' : ''})</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Vencimiento 1ra Cuota *</label>
                                                <input type="date" value={formulario.vence} onChange={(e) => setFormulario(f => ({...f, vence: e.target.value}))} className="form-input" />
                                            </div>
                                            <div className="form-group">
                                                <label>Tipo de Entrega</label>
                                                <div className="cuenta-selector" ref={dropdownEntregaRef}>
                                                    {entregaSeleccionada ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{entregaSeleccionada.id_tipoent} - {entregaSeleccionada.nom_entrega}</span>
                                                            <button type="button" onClick={() => { setEntregaSeleccionada(null); setFormulario(f => ({...f, entrega:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar tipo de entrega..." value={busquedaEntrega} onChange={(e) => setBusquedaEntrega(e.target.value)} onFocus={() => setDropdownEntregaAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownEntregaAbierto && !entregaSeleccionada && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarEntregas(busquedaEntrega).map(te => (
                                                                <div key={te.id_tipoent} className="cuenta-item" onClick={() => seleccionarEntrega(te)}><strong>{te.id_tipoent}</strong> - {te.nom_entrega}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Vendedor</label>
                                                <div className="cuenta-selector" ref={dropdownVendedorRef}>
                                                    {vendedorSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{vendedorSeleccionado.idVendedor} - {vendedorSeleccionado.nomVendedor}</span>
                                                            <button type="button" onClick={() => { setVendedorSeleccionado(null); setFormulario(f => ({...f, idVendedor:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar vendedor..." value={busquedaVendedor} onChange={(e) => setBusquedaVendedor(e.target.value)} onFocus={() => setDropdownVendedorAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownVendedorAbierto && !vendedorSeleccionado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarVendedores(busquedaVendedor).map(v => (
                                                                <div key={v.idVendedor} className="cuenta-item" onClick={() => seleccionarVendedor(v)}><strong>{v.idVendedor}</strong> - {v.nomVendedor}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Moneda *</label>
                                                <select value={formulario.idMoneda} onChange={(e) => { const es = e.target.value === 'GS'; setFormulario(f => ({...f, idMoneda: e.target.value, cambio: es ? 1 : 0})); }}
                                                    style={{ width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'5px' }}>
                                                    <option value="">-- Seleccione --</option>
                                                    {monedas.map(m => (<option key={m.idMoneda} value={m.idMoneda}>{m.idMoneda} - {m.nomMoneda}</option>))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Tipo de Cambio *</label>
                                                <input type="number" value={formulario.cambio} onChange={(e) => setFormulario(f => ({...f, cambio: Number(e.target.value)}))} disabled={formulario.idMoneda === 'GS'}
                                                    style={{ width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'5px', backgroundColor: formulario.idMoneda === 'GS' ? '#f0f0f0' : 'white' }} />
                                            </div>
                                            <div className="form-group">
                                                <label>Deposito *</label>
                                                <select value={formulario.idDeposito} onChange={(e) => setFormulario(f => ({...f, idDeposito: Number(e.target.value)}))}
                                                    style={{ width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'5px' }}>
                                                    <option value="">-- Seleccione --</option>
                                                    {depositos.filter(d => d.reserva !== 'S').map(d => (<option key={d.idDeposito} value={d.idDeposito}>{d.idDeposito} - {d.nomDepto}</option>))}
                                                </select>
                                            </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Observaciones</label>
                                        <textarea value={formulario.obs} onChange={(e) => setFormulario(f => ({...f, obs: e.target.value}))} maxLength={100} className="form-input" rows="2" />
                                    </div>
                                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#FFF8E1', borderRadius: '5px' }}>
                                        <input type="checkbox" id="entregaRem"
                                            checked={formulario.entregarPorRemision || false}
                                            onChange={(e) => setFormulario(f => ({...f, entregarPorRemision: e.target.checked}))} />
                                        <label htmlFor="entregaRem" style={{ margin: 0, cursor: 'pointer', fontSize: '13px' }}>
                                            📦 Entregar por Nota de Envío — <span style={{ color: '#666' }}>el stock sale al remitir (la factura no descuenta ahora)</span>
                                        </label>
                                    </div>
                                </div>

                                {/* ===== DETALLE (siempre visible) ===== */}
                                <div className="form-section" style={{ marginTop: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>Items de la Factura</h3>
                                        <button type="button" onClick={abrirModalRemisiones} className="btn btn-exportar"
                                            disabled={!formulario.idCliente}
                                            title={!formulario.idCliente ? 'Primero seleccione un cliente' : 'Cargar items desde remisiones sin facturar'}>
                                            Obtener Remisiones
                                        </button>
                                    </div>
                                    {remisionesFacturadas.length > 0 && (
                                        <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#EDF1FB', borderRadius: '5px', fontSize: '13px', color: '#001A70' }}>
                                            Esta factura se generara desde <strong>{remisionesFacturadas.length}</strong> remision(es): {remisionesFacturadas.map(r => r.nremision).join(', ')}
                                        </div>
                                    )}

                                    {/* Fila inline para agregar */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div className="form-group">
                                            <label>Producto *</label>
                                            <div className="cuenta-selector" ref={dropdownProductoRef}>
                                                {productoSeleccionado ? (
                                                    <div className="cuenta-seleccionada">
                                                        <span><strong>{productoSeleccionado.id_producto}</strong> - {productoSeleccionado.descripcion}</span>
                                                        <button type="button" onClick={() => { setProductoSeleccionado(null); setBusquedaProducto(''); setDetalleTemp({cantidad:1,precio:0,porcDescuento:0,gravada:0,costoprom:0,idPresent:'UN',idGrupo:null}); }} className="btn-limpiar">✕</button>
                                                    </div>
                                                ) : (
                                                    <input type="text" placeholder="Buscar producto..." value={busquedaProducto}
                                                        onChange={(e) => { setBusquedaProducto(e.target.value); buscarProductoAPI(e.target.value); }}
                                                        onFocus={() => setDropdownProductoAbierto(true)} className="form-input" />
                                                )}
                                                {dropdownProductoAbierto && !productoSeleccionado && productosEncontrados.length > 0 && (
                                                    <div className="cuenta-dropdown">
                                                        {productosEncontrados.map((p, i) => (
                                                            <div key={i} className="cuenta-item" onClick={() => seleccionarProductoDetalle(p)}>
                                                                <strong>{p.id_producto}</strong> - {p.descripcion} {p.gravada > 0 && <span style={{color:'#7f8c8d',fontSize:'11px'}}>(IVA {p.gravada}%)</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Cantidad</label>
                                            <input type="number" value={detalleTemp.cantidad} min="1" step="1" onChange={(e) => setDetalleTemp(d => ({...d, cantidad: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <label>Precio (c/IVA)</label>
                                            <input type="number" value={detalleTemp.precio} min="0" step="100" onChange={(e) => setDetalleTemp(d => ({...d, precio: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <label>Desc %</label>
                                            <input type="number" value={detalleTemp.porcDescuento} min="0" max="100" step="0.5" onChange={(e) => setDetalleTemp(d => ({...d, porcDescuento: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <button type="button" onClick={agregarDetalle} className="btn btn-nuevo" style={{ marginTop: '5px' }} disabled={!productoSeleccionado}>+ Agregar</button>
                                        </div>
                                    </div>

                                    {/* Preview */}
                                    {previewLinea && productoSeleccionado && (
                                        <div style={{ display: 'flex', gap: '20px', padding: '8px 15px', backgroundColor: '#EDF1FB', borderRadius: '6px', marginTop: '5px', fontSize: '13px' }}>
                                            <span>Exenta: <strong>{formatearNumero(previewLinea.monExenta)}</strong></span>
                                            <span>Gravada 5%: <strong>{formatearNumero(previewLinea.monGravada5)}</strong></span>
                                            <span>Gravada 10%: <strong>{formatearNumero(previewLinea.monGravada10)}</strong></span>
                                            <span>IVA: <strong>{formatearNumero(previewLinea.monIva)}</strong></span>
                                            <span style={{ color: '#2DCCD3' }}>Total linea: <strong>{formatearNumero(previewLinea.total)}</strong></span>
                                        </div>
                                    )}

                                    {/* Tabla items */}
                                    <div style={{ marginTop: '15px' }}>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead><tr><th>Producto</th><th>Descripcion</th><th style={{textAlign:'right'}}>Cant</th><th style={{textAlign:'right'}}>Precio</th><th style={{textAlign:'right'}}>Desc%</th><th style={{textAlign:'right'}}>Exenta</th><th style={{textAlign:'right'}}>Gravada 5%</th><th style={{textAlign:'right'}}>Gravada 10%</th><th style={{textAlign:'right'}}>IVA</th><th style={{textAlign:'right'}}>Total</th><th>Acc</th></tr></thead>
                                            <tbody>
                                                {detalles.length === 0 ? (
                                                    <tr><td colSpan="11" className="sin-datos">No hay items agregados</td></tr>
                                                ) : detalles.map((d, i) => {
                                                    const totalLinea = Number(d.monExenta) + Number(d.monGravada5) + Number(d.monGravada10) + Number(d.monIva);
                                                    return (
                                                    <tr key={i}>
                                                        <td>{d.idProducto}</td><td>{d.nomProducto}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.cantidad)}</td>
                                                        <td style={{textAlign:'right'}}>
                                                            <input type="number" value={d.precio} min="0" step="100"
                                                                onChange={(e) => editarPrecioDetalle(d.idProducto, Number(e.target.value))}
                                                                style={{ width: '110px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'right' }} />
                                                        </td>
                                                        <td style={{textAlign:'right'}}>{d.porcDescuento > 0 ? d.porcDescuento.toFixed(2)+'%' : '0'}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monExenta)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monGravada5)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monGravada10)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monIva)}</td>
                                                        <td style={{textAlign:'right', fontWeight: 'bold', color: '#001A70'}}>{formatearNumero(totalLinea)}</td>
                                                        <td><button type="button" onClick={() => eliminarDetalle(d.idProducto)} className="btn-accion" title="Eliminar">🗑</button></td>
                                                    </tr>);
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                    <td colSpan="5" style={{textAlign:'right'}}>TOTALES:</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.exenta)}</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.gravada5)}</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.gravada10)}</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.iva)}</td>
                                                    <td style={{textAlign:'right', color: '#2DCCD3'}}>{formatearNumero(totales.total)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                {/* Botones */}
                                <div className="form-actions">
                                    <button type="button" onClick={cerrarModal} className="btn-secondary">Cancelar</button>
                                    <button type="submit" className="btn-primary">Guardar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ====== MODAL VER FACTURA ====== */}
            {modalVerAbierto && facturaVer && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '1100px' }}>
                        <div className="modal-header">
                            <h3>Factura Nro {facturaVer.nfactura} - {facturaVer.tipoDoc}</h3>
                            <button onClick={() => setModalVerAbierto(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Cabecera readonly */}
                            <div className="form-section">
                                <h3>Datos de Cabecera</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '14px' }}>
                                    <div><strong>Timbrado:</strong> {facturaVer.timbrado}</div>
                                    <div><strong>Sucursal:</strong> {facturaVer.idSucursal}</div>
                                    <div><strong>Boca:</strong> {facturaVer.boca}</div>
                                    <div><strong>Tipo Doc:</strong> {facturaVer.tipoDoc}</div>
                                    <div><strong>Nro Factura:</strong> {facturaVer.nfactura}</div>
                                    <div><strong>Fecha:</strong> {facturaVer.fecha ? formatearFecha(facturaVer.fecha) : ''}</div>
                                    <div><strong>Cliente:</strong> {facturaVer.nomCliente}</div>
                                    <div><strong>RUC:</strong> {facturaVer.rucCliente}</div>
                                    <div><strong>Vendedor:</strong> {facturaVer.idVendedor}</div>
                                    <div><strong>Forma Pago:</strong> {facturaVer.idFormapago}</div>
                                    <div><strong>Moneda:</strong> {facturaVer.idMoneda}</div>
                                    <div><strong>Cambio:</strong> {facturaVer.cambio}</div>
                                    <div><strong>Vencimiento:</strong> {facturaVer.vence ? formatearFecha(facturaVer.vence) : ''}</div>
                                    <div><strong>Estado:</strong> {facturaVer.estado === 'C' ? 'Cerrada' : facturaVer.estado === ' ' ? 'Abierta' : facturaVer.estado}</div>
                                    <div><strong>Deposito:</strong> {facturaVer.idDeposito}</div>
                                </div>
                                {facturaVer.obs && <div style={{ marginTop: '10px' }}><strong>Obs:</strong> {facturaVer.obs}</div>}
                                {facturaVer.numLet && facturaVer.numLet.trim() && <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px', fontStyle: 'italic', fontSize: '13px' }}><strong>Importe en letras:</strong> {facturaVer.numLet}</div>}
                            </div>

                            {/* Detalle */}
                            <div className="form-section" style={{ marginTop: '20px' }}>
                                <h3>Items</h3>
                                <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                    <thead><tr><th>Item</th><th>Producto</th><th>Descripcion</th><th style={{textAlign:'right'}}>Cant</th><th style={{textAlign:'right'}}>Precio</th><th style={{textAlign:'right'}}>Desc%</th><th style={{textAlign:'right'}}>Exenta</th><th style={{textAlign:'right'}}>Gravada 5%</th><th style={{textAlign:'right'}}>Gravada 10%</th><th style={{textAlign:'right'}}>IVA</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
                                    <tbody>
                                        {detallesVer.length === 0 ? (
                                            <tr><td colSpan="11" className="sin-datos">Sin items</td></tr>
                                        ) : detallesVer.map((d, i) => {
                                            const grav = Number(d.monGravada) || 0;
                                            const iva5 = grav > 0 && Number(d.monIva) > 0 && Math.abs(grav * 0.05 - Number(d.monIva)) < Math.abs(grav * 0.10 - Number(d.monIva));
                                            const totalLinea = Number(d.monExenta) + grav + Number(d.monIva);
                                            return (
                                            <tr key={i}>
                                                <td>{d.item}</td><td>{d.idProducto}</td><td>{d.nomProducto}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.cantidad)}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.precio)}</td>
                                                <td style={{textAlign:'right'}}>{Number(d.porcDescuento) > 0 ? Number(d.porcDescuento).toFixed(2)+'%' : '0'}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.monExenta)}</td>
                                                <td style={{textAlign:'right'}}>{iva5 ? formatearNumero(d.monGravada) : '0'}</td>
                                                <td style={{textAlign:'right'}}>{!iva5 && grav > 0 ? formatearNumero(d.monGravada) : '0'}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.monIva)}</td>
                                                <td style={{textAlign:'right', fontWeight: 'bold', color: '#001A70'}}>{formatearNumero(totalLinea)}</td>
                                            </tr>);
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                            <td colSpan="6" style={{textAlign:'right'}}>TOTALES:</td>
                                            <td style={{textAlign:'right'}}>{formatearNumero(detallesVer.reduce((s, d) => s + Number(d.monExenta), 0))}</td>
                                            <td style={{textAlign:'right'}}>{formatearNumero(detallesVer.filter(d => { const g=Number(d.monGravada)||0; return g>0 && Math.abs(g*0.05-Number(d.monIva)) < Math.abs(g*0.10-Number(d.monIva)); }).reduce((s,d) => s+Number(d.monGravada),0))}</td>
                                            <td style={{textAlign:'right'}}>{formatearNumero(detallesVer.filter(d => { const g=Number(d.monGravada)||0; return g>0 && Math.abs(g*0.10-Number(d.monIva)) <= Math.abs(g*0.05-Number(d.monIva)); }).reduce((s,d) => s+Number(d.monGravada),0))}</td>
                                            <td style={{textAlign:'right'}}>{formatearNumero(detallesVer.reduce((s, d) => s + Number(d.monIva), 0))}</td>
                                            <td style={{textAlign:'right', color: '#2DCCD3'}}>{formatearNumero(detallesVer.reduce((s, d) => s + Number(d.monExenta) + Number(d.monGravada) + Number(d.monIva), 0))}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Totales generales */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '25px', marginTop: '15px', padding: '15px', backgroundColor: '#EDF1FB', borderRadius: '8px', fontSize: '15px' }}>
                                <div><strong>Exenta:</strong> {formatearNumero(facturaVer.monExenta)}</div>
                                <div><strong>Gravada:</strong> {formatearNumero(facturaVer.monGravada)}</div>
                                <div><strong>IVA:</strong> {formatearNumero(facturaVer.monIva)}</div>
                                <div style={{ color: '#001A70', fontSize: '18px' }}><strong>TOTAL: {formatearNumero(facturaVer.monTotal)}</strong></div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '15px' }}>
                                <button type="button" onClick={() => setModalVerAbierto(false)} className="btn-primary">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== MODAL VER NOTAS DE ENVIO (REMISIONES) DE UNA FACTURA ===== */}
            {modalNotasEnvioAbierto && facturaNotasEnvio && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '950px' }}>
                        <div className="modal-header">
                            <h3>Notas de Envio - Factura Nro {facturaNotasEnvio.nfactura}</h3>
                            <button onClick={() => setModalNotasEnvioAbierto(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            {notasEnvio.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    Esta factura no fue generada desde remisiones (no tiene notas de envio asociadas)
                                </div>
                            ) : (
                                notasEnvio.map((rem, idx) => {
                                    const k = `${rem.id_sucursal}-${rem.boca}-${rem.nremision}`;
                                    const items = detalleNotaEnvio[k] || [];
                                    return (
                                        <div key={idx} className="form-section" style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
                                            <h3 style={{ color: '#001A70', borderBottom: '2px solid #EDF1FB', paddingBottom: '8px' }}>
                                                Nota de Remision Nro {String(rem.id_sucursal).padStart(3,'0')}-{String(rem.boca).padStart(3,'0')}-{rem.nremision}
                                            </h3>

                                            {/* Cabecera */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', fontSize: '13px', marginBottom: '10px' }}>
                                                <div><strong>Fecha:</strong> {rem.fecha ? formatearFecha(rem.fecha) : ''}</div>
                                                <div><strong>Fecha Fin:</strong> {rem.fechafin ? formatearFecha(rem.fechafin) : ''}</div>
                                                <div><strong>Timbrado:</strong> {rem.timbrado}</div>
                                                <div><strong>Estado:</strong> {rem.estado === 'A' ? 'Anulada' : 'Activa'}</div>
                                            </div>

                                            {/* Cliente */}
                                            <div style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>
                                                <div style={{ fontWeight: 'bold', color: '#001A70', marginBottom: '5px', fontSize: '13px' }}>Cliente</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                                    <div><strong>Cliente:</strong> [{rem.id_cliente}] {rem.nom_cliente}</div>
                                                    <div><strong>RUC:</strong> {rem.ruc_cliente}</div>
                                                    <div><strong>Telefono:</strong> {rem.tel_cliente}</div>
                                                </div>
                                            </div>

                                            {/* Transporte */}
                                            <div style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>
                                                <div style={{ fontWeight: 'bold', color: '#001A70', marginBottom: '5px', fontSize: '13px' }}>Transporte</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                                    <div><strong>Dir. Partida:</strong> {rem.dir_partida}</div>
                                                    <div><strong>Dir. Llegada:</strong> {rem.dir_llegada}</div>
                                                    <div><strong>Transportista:</strong> {rem.nom_transportista} {rem.ruc_transportista ? '(' + rem.ruc_transportista + ')' : ''}</div>
                                                    <div><strong>Conductor:</strong> {rem.nom_conductor} {rem.ci_conductor ? '(CI: ' + rem.ci_conductor + ')' : ''}</div>
                                                    <div><strong>Marca Vehiculo:</strong> {rem.marca_vehiculo}</div>
                                                    <div><strong>Chapa:</strong> {rem.chapa}</div>
                                                </div>
                                            </div>

                                            {rem.obs && <div style={{ fontSize: '13px', marginBottom: '10px' }}><strong>Obs:</strong> {rem.obs}</div>}

                                            {/* Detalle */}
                                            <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                                <thead><tr><th>Item</th><th>Producto</th><th>Descripcion</th><th style={{textAlign:'right'}}>Cantidad</th><th style={{textAlign:'right'}}>Precio</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
                                                <tbody>
                                                    {items.length === 0 ? (
                                                        <tr><td colSpan="6" className="sin-datos">Sin items</td></tr>
                                                    ) : items.map((it, j) => (
                                                        <tr key={j}>
                                                            <td>{it.item}</td>
                                                            <td>{it.id_producto}</td>
                                                            <td>{it.descripcion}</td>
                                                            <td style={{textAlign:'right'}}>{formatearNumero(it.cantidad)}</td>
                                                            <td style={{textAlign:'right'}}>{formatearNumero(it.precio)}</td>
                                                            <td style={{textAlign:'right'}}>{formatearNumero(it.total)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })
                            )}
                            <div className="form-actions" style={{ marginTop: '15px' }}>
                                <button type="button" onClick={() => setModalNotasEnvioAbierto(false)} className="btn-primary">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== MODAL SELECCION DE REMISIONES ===== */}
            {modalRemisionesAbierto && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h3>Remisiones sin facturar - {formulario.nomCliente}</h3>
                            <button onClick={() => setModalRemisionesAbierto(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            {remisionesDisponibles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    El cliente no tiene remisiones pendientes de facturar
                                </div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '10px' }}>
                                        <button type="button" onClick={toggleTodasRemisiones} className="btn btn-nuevo" style={{ fontSize: '13px' }}>
                                            {remisionesMarcadas.length === remisionesDisponibles.length ? 'Desmarcar todas' : 'Marcar todas'}
                                        </button>
                                        <span style={{ marginLeft: '15px', fontSize: '13px', color: '#7f8c8d' }}>
                                            {remisionesMarcadas.length} de {remisionesDisponibles.length} seleccionada(s)
                                        </span>
                                    </div>
                                    <div className="tabla-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead><tr>
                                                <th>Sel</th><th>Nro Remision</th><th>Fecha</th><th>Items</th>
                                                <th style={{textAlign:'right'}}>Total Estimado</th><th>Dir. Llegada</th><th>Obs</th>
                                            </tr></thead>
                                            <tbody>
                                                {remisionesDisponibles.map((r, i) => {
                                                    const k = remKey(r);
                                                    const marcada = remisionesMarcadas.includes(k);
                                                    return (
                                                        <tr key={i} onClick={() => toggleRemision(r)}
                                                            style={{ cursor: 'pointer', backgroundColor: marcada ? '#EDF1FB' : '' }}>
                                                            <td><input type="checkbox" checked={marcada} readOnly /></td>
                                                            <td>{String(r.id_sucursal).padStart(3,'0')}-{String(r.boca).padStart(3,'0')}-{r.nremision}</td>
                                                            <td>{r.fecha ? formatearFecha(r.fecha) : ''}</td>
                                                            <td style={{textAlign:'center'}}>{r.cant_items}</td>
                                                            <td style={{textAlign:'right'}}>{formatearNumero(r.total_estimado)}</td>
                                                            <td>{r.dir_llegada}</td>
                                                            <td>{r.obs}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <div className="form-actions" style={{ marginTop: '15px' }}>
                                <button type="button" onClick={() => setModalRemisionesAbierto(false)} className="btn-secondary">Cancelar</button>
                                <button type="button" onClick={confirmarRemisiones} className="btn-primary"
                                    disabled={remisionesMarcadas.length === 0}>
                                    Cargar Items ({remisionesMarcadas.length})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Modal: Generar Nota de Envío desde la factura ===== */}
            {modalGenRemAbierto && facturaGenRem && (
                <div className="modal-overlay" onClick={() => setModalGenRemAbierto(false)}>
                    <div className="modal-contenido" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '860px', width: '95%' }}>
                        <div className="modal-header">
                            <h3>📦 Generar Nota de Envío — Factura {facturaGenRem.tipoDoc} {facturaGenRem.nfactura}</h3>
                            <button onClick={() => setModalGenRemAbierto(false)} className="btn-cerrar">✖</button>
                        </div>
                        <div style={{ padding: '15px' }}>
                            {/* Datos bloqueados: cliente + relacion con factura */}
                            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '8px 10px',
                                          background: '#EEF3FB', borderRadius: '5px', marginBottom: '12px', fontSize: '13px' }}>
                                <div>🔒 <strong>Cliente:</strong> {facturaGenRem.idCliente} - {facturaGenRem.nomCliente} ({facturaGenRem.rucCliente})</div>
                                <div>🔒 <strong>Factura:</strong> {facturaGenRem.tipoDoc} {facturaGenRem.nfactura} (Boca {facturaGenRem.boca})</div>
                            </div>

                            {/* Datos de la Nota de Envio (editables) */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '12px' }}>
                                <div>
                                    <label style={lblRem}>Timbrado de Remisión *</label>
                                    <select value={remTimbradoSel ? `${remTimbradoSel.timbrado}|${remTimbradoSel.boca}` : ''}
                                        onChange={(e) => {
                                            if (!e.target.value) { setRemTimbradoSel(null); return; }
                                            const [timbrado, boca] = e.target.value.split('|');
                                            setRemTimbradoSel({ timbrado, boca: Number(boca) });
                                        }} style={inpRem}>
                                        <option value="">-- Seleccione --</option>
                                        {timbradosRem.map((t, i) => (
                                            <option key={i} value={`${t.timbrado}|${t.boca}`}>{t.timbrado} | Boca {t.boca}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={lblRem}>Motivo</label>
                                    <select value={formRem.idMotivo} onChange={(e) => setFormRem(r => ({ ...r, idMotivo: e.target.value }))} style={inpRem}>
                                        <option value="">-- Seleccione --</option>
                                        {motivosRem.map(m => (<option key={m.idMotivo || m.id_motivo} value={m.idMotivo || m.id_motivo}>{m.nomMotivo || m.nom_motivo}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label style={lblRem}>Otro Motivo</label>
                                    <input type="text" value={formRem.otroMotivo} maxLength={60} onChange={(e) => setFormRem(r => ({ ...r, otroMotivo: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Fecha Fin (traslado)</label>
                                    <input type="date" value={formRem.fechafin} onChange={(e) => setFormRem(r => ({ ...r, fechafin: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Dir. Partida</label>
                                    <input type="text" value={formRem.dirPartida} maxLength={60} onChange={(e) => setFormRem(r => ({ ...r, dirPartida: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Dir. Llegada</label>
                                    <input type="text" value={formRem.dirLlegada} maxLength={60} onChange={(e) => setFormRem(r => ({ ...r, dirLlegada: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Transportista</label>
                                    <input type="text" value={formRem.nomTransportista} maxLength={60} onChange={(e) => setFormRem(r => ({ ...r, nomTransportista: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>RUC Transportista</label>
                                    <input type="text" value={formRem.rucTransportista} maxLength={12} onChange={(e) => setFormRem(r => ({ ...r, rucTransportista: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Conductor</label>
                                    <input type="text" value={formRem.nomConductor} maxLength={60} onChange={(e) => setFormRem(r => ({ ...r, nomConductor: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>CI Conductor</label>
                                    <input type="text" value={formRem.ciConductor} maxLength={15} onChange={(e) => setFormRem(r => ({ ...r, ciConductor: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Marca Vehículo</label>
                                    <input type="text" value={formRem.marcaVehiculo} maxLength={30} onChange={(e) => setFormRem(r => ({ ...r, marcaVehiculo: e.target.value }))} style={inpRem} />
                                </div>
                                <div>
                                    <label style={lblRem}>Chapa</label>
                                    <input type="text" value={formRem.chapa} maxLength={7} onChange={(e) => setFormRem(r => ({ ...r, chapa: e.target.value.toUpperCase() }))} style={inpRem} />
                                </div>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={lblRem}>Observaciones</label>
                                <input type="text" value={formRem.obs} maxLength={100} onChange={(e) => setFormRem(r => ({ ...r, obs: e.target.value }))} style={inpRem} />
                            </div>

                            <h4 style={{ margin: '10px 0 6px', color: '#004F9F' }}>Items a remitir</h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                    <thead>
                                        <tr>
                                            <th>Cod</th><th>Descripción</th>
                                            <th style={{ textAlign: 'right' }}>Facturado</th>
                                            <th style={{ textAlign: 'right' }}>Remitido</th>
                                            <th style={{ textAlign: 'right' }}>Pendiente</th>
                                            <th style={{ textAlign: 'right' }}>A remitir</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itemsGenRem.map(it => {
                                            const k = String(it.id_producto).trim();
                                            const pend = Number(it.pendiente) || 0;
                                            return (
                                                <tr key={k}>
                                                    <td>{k}</td>
                                                    <td>{it.descripcion}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(it.facturado)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(it.remitido)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatearNumero(pend)}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <input type="number" min="0" max={pend} step="any"
                                                            value={cantidadesGenRem[k] ?? ''}
                                                            onChange={(e) => setCantidadesGenRem(c => ({ ...c, [k]: e.target.value }))}
                                                            disabled={pend <= 0}
                                                            style={{ width: '90px', padding: '4px', textAlign: 'right', border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {itemsGenRem.length === 0 && (
                                            <tr><td colSpan="6" style={{ textAlign: 'center', color: '#888', padding: '15px' }}>Cargando items...</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={() => setModalGenRemAbierto(false)} className="btn btn-exportar" style={{ background: '#9e9e9e' }}>Cancelar</button>
                                <button type="button" onClick={generarRemision} disabled={guardandoRem} className="btn btn-nuevo">
                                    {guardandoRem ? 'Generando...' : 'Generar Nota de Envío'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const lblRem = { fontSize: '11px', color: '#555', display: 'block', marginBottom: '2px' };
const inpRem = { width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' };

export default FFacturaVentas;
